import {
  Box,
  CircularProgress,
  Grid,
  Modal,
  Table,
  useMediaQuery
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import { useTheme } from '@mui/material/styles'
import { styled } from '@mui/material/styles'
import TableBody from '@mui/material/TableBody'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { useNavigate } from 'react-router-dom'

import { TextBold, TextRegular } from '../../commonComponents/TextStyling'
import { logoutUser } from '../../actions/loginActions'
import serverData from '../../api/ServerData'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import NoRecordFound from '../../commonComponents/NoRecordFound'
import { currencySymbol } from '../../utils/AppConstants'
import { formatNumber, isEmpty } from '../../utils/AppHelper'
import TabViewComponent from '../../commonComponents/TabViewComponent'
import TotalEarnings from '../../commonComponents/ResponsiveComponents/TotalEarnings'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.opposite,
    fontSize: 16,
    fontWeight: 700
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    fontWeight: 600
  }
}))

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3
}

export default function ReferralEarning () {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(true)

  const [rowsData, setRowsData] = useState({
    potentialReferrals: [],
    successfulReferrals: [],
    totalEarnings: ''
  })

  const matches = useMediaQuery(theme.breakpoints.down('sm')) // Detect small screens

  useEffect(function () {
    getReferralEarnings()
  }, [])

  function getReferralEarnings () {
    serverData.getReferralEarnings(
      {},
      result => {
        if (result.status) {
          setRowsData(result?.response)
        }
        setLoading(false)
      },
      error => {
        setLoading(false)
        if (error.status === 401) {
          dispatch(logoutUser(navigate)) // eslint-disable-next-line
        }
      }
    )
  }

  return (
    <Grid container spacing={0}>
      <Grid item xs={12} style={{ display: 'flex' }}>
        {matches ? (
          <TotalEarnings data={rowsData.totalEarnings} />
        ) : (
          <TextBold style={{ fontSize: '24px' }}>Referral earnings</TextBold>
        )}
      </Grid>

      {loading ? (
        <Grid
          container
          direction={'column'}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            width: '100%'
          }}
        >
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : (
        <TabViewComponent
          style={{
            margin: '24px 0px'
          }}
          rowsData={rowsData}
          tabs={[
            {
              id: 0,
              label: 'Potential referral earnings',
              content: (
                <EarningsContent
                  data={rowsData.potentialReferrals}
                  theme={theme}
                  matches={matches}
                />
              )
            },
            {
              id: 1,
              label: 'Successful referral earnings',
              content: (
                <EarningsContent
                  data={rowsData.successfulReferrals}
                  theme={theme}
                  matches={matches}
                />
              )
            }
          ]}
        />
      )}
    </Grid>
  )
}

function EarningsContent ({ data, theme, matches }) {
  const [modal, setModal] = useState({ open: false })

  return data.length > 0 ? (
    <>
      <TableContainer component={Paper} style={{ borderRadius: '16px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Employer Name</StyledTableCell>
              <StyledTableCell>Candidate Name</StyledTableCell>
              <StyledTableCell>Job Title</StyledTableCell>
              <StyledTableCell>Joining Date</StyledTableCell>
              <StyledTableCell>Guarantee Period</StyledTableCell>
              <StyledTableCell>Due On</StyledTableCell>
              <StyledTableCell>Placement Fee</StyledTableCell>
              <StyledTableCell>Placement Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map(row => (
              <TableRow
                key={row.employerName}
                sx={{
                  '&:last-child td, &:last-child th': {
                    border: 0
                  }
                }}
              >
                <StyledTableCell component='th' scope='row'>
                  {row.companyname}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    color: theme.palette.primary.main,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setModal({
                      open: true,
                      item: row,
                      onClose: () => {
                        setModal({ open: false })
                      }
                    })
                  }}
                >
                  {row.candidateFirstName} {row.candidateLastName}
                </StyledTableCell>
                <StyledTableCell>{row.jobTitle}</StyledTableCell>
                <StyledTableCell>
                  {moment(row.joiningDate).format('MM/DD/YYYY')}
                </StyledTableCell>
                <StyledTableCell>
                  {moment(row.guaranteePeriodEndDate).format('MM/DD/YYYY')}
                </StyledTableCell>
                <StyledTableCell>
                  {moment(row.dueOn).format('MM/DD/YYYY')}
                </StyledTableCell>
                <StyledTableCell>
                  {row.paymentStatus === 'paid'
                    ? formatNumber(row.amountPaid)
                    : row.referralRewardType === 'fixed'
                    ? `${currencySymbol}${formatNumber(
                        row.referralRewardAmount
                      )}`
                    : row.referralRewardType === 'percentage'
                    ? `${row.referralRewardAmount}% of base salary`
                    : row.referralRewardType === 'hourly'
                    ? `${currencySymbol}${row.referralRewardAmount} per hour`
                    : ''}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    color:
                      row.paymentStatus === 'paid'
                        ? theme.palette.text.success
                        : row.paymentStatus === 'inprocess'
                        ? theme.palette.text.yellow
                        : theme.palette.text.error
                  }}
                >
                  {row.paymentStatus === 'inprocess'
                    ? 'Payment in process'
                    : row.paymentStatus?.toUpperCase()}
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {modal.open && <DetailsModal data={modal} matches={matches} />}
    </>
  ) : (
    <NoRecordFound />
  )
}

function DetailsModal ({ data, matches }) {
  const theme = useTheme()
  var { item, onClose } = data
  var listData = [
    {
      label: 'Applied on',
      value: moment(item.createdAt).format('D MMMM YYYY, dddd')
    }
  ]

  if (item.managerReviewDate) {
    listData.push({
      label: 'Manager review date',
      value: moment(item.managerReviewDate).format('D MMMM YYYY, dddd')
    })
  }

  if (item.interviewStageDate) {
    listData.push({
      label: 'Interview date',
      value: moment(item.interviewStageDate).format('D MMMM YYYY, dddd')
    })
  }

  if (item.offerStageDate) {
    listData.push({
      label: 'Offer date',
      value: moment(item.offerStageDate).format('D MMMM YYYY, dddd')
    })
  }

  if (item.hiredOnDate) {
    listData.push({
      label: 'Hired on',
      value: moment(item.hiredOnDate).format('D MMMM YYYY, dddd')
    })
  }

  if (item.joiningDate) {
    listData.push({
      label: 'Joining date',
      value: moment(item?.joiningDate).format('D MMMM YYYY, dddd')
    })
  }

  if (item.baseSalary) {
    listData.push({
      label: 'Base salary',
      value: `${currencySymbol} ${formatNumber(item.baseSalary)}`
    })
  }

  if (!isEmpty(item.isJoined)) {
    listData.push({
      label: 'Joining status',
      value: item.isJoined ? 'Joined' : 'Not joined',
      color: item.isJoined
        ? theme.palette.text.success
        : theme.palette.text.error
    })
  }

  if (item.leftOutDate) {
    listData.push({
      label: 'Left out on',
      value: moment(item?.leftOutDate).format('D MMMM YYYY, dddd')
    })
  }

  return (
    <Modal open={true} onClose={onClose}>
      <Box sx={{ ...style, width: matches ? 350 : 600, borderRadius: '8px' }}>
        <Grid container>
          {listData.map((row, index) => (
            <Grid item xs={6} style={{ marginTop: 16 }}>
              <TextBold style={{ fontSize: '16px' }}>{row.label} :</TextBold>
              <div>
                <TextRegular
                  style={{
                    fontSize: '16px',
                    color: row.color
                  }}
                >
                  {row.value}
                </TextRegular>
              </div>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Modal>
  )
}
